import React, { useState } from "react";
import { toast } from "sonner";

import { useAgentUser, useSetAgentUserStatus } from "@/shared/api/onboarding";
import { AppLogo } from "@/shared/components/AppLogo";
import { GhostButton } from "@/shared/components/ui-kit";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/shared/components/ui/sheet";
import { ViaIcon } from "@/shared/components/ViaIcon";
import { ago } from "@/shared/lib/time";
import { questionsOf, said, verifiedPhone } from "../blocks";
import { glyphOf, nameOf } from "../knownOn";
import { PersonConfirm, type PersonAction } from "./PersonConfirm";
import { StatusPill } from "./StatusPill";

import type { AgentUser, AgentUserDetail, OnboardingFlow } from "@/shared/types";

/* One person, opened from the table: what they told onboarding and the phone they verified,
   the email they signed in with, the policy they land on, where they are known, the apps they connected their own
   accounts of, and their conversations — and the same three actions as the row. */

const connectedAs = ({ status, account }: AgentUserDetail["apps"][number]) =>
  status === "connected" ? (account ? `Connected as ${account}` : "Connected")
    : status === "connecting" ? "Not finished connecting" : status === "expired" ? "Expired" : "Didn't connect";
export function PersonSheet({ person, flow, onClose, onOpenThread }: {
  person: AgentUser;
  flow: OnboardingFlow | undefined;
  onClose: () => void;
  onOpenThread?: (threadId: string) => void;
}) {
  const detail = useAgentUser(person.id);
  const setStatus = useSetAgentUserStatus();
  const [confirm, setConfirm] = useState<PersonAction | null>(null);
  const shown = detail.data ?? person;
  const fields = questionsOf(flow?.blocks ?? []);
  const labelled = new Set(fields.map((f) => f.key));
  // answers to questions no longer in the flow are still theirs
  const others = Object.entries(shown.answers).filter(([key]) => !labelled.has(key));
  const phone = verifiedPhone(flow?.blocks ?? [], shown.proved);

  return (
    <Sheet open onOpenChange={(open) => !open && onClose()}>
      <SheetContent className="w-full sm:max-w-[420px] p-0 bg-[var(--canvas)] border-l border-[var(--hairline-strong)] flex flex-col">
        <div className="px-5 pt-5 pb-4 border-b hairline pr-12">
          <SheetTitle className="text-[15px] font-medium tracking-[-0.01em] truncate">{shown.name ?? shown.email ?? "Unknown"}</SheetTitle>
          <SheetDescription asChild>
            <div className="mt-1.5 flex items-center gap-2 text-[12px] text-[var(--text-tertiary)]">
              <StatusPill person={shown} />
              <span>
                First seen <span className="font-mono tabular-nums">{ago(shown.first_seen)}</span>
              </span>
            </div>
          </SheetDescription>
        </div>

        <div className="flex-1 min-h-0 overflow-y-auto px-5 py-4 space-y-6">
          <Section title="Details">
            {fields.map((f) => <Line key={f.key ?? f.label} label={f.label} value={said(shown.answers[f.key ?? ""])} />)}
            {others.map(([key, value]) => <Line key={key} label={key} value={said(value)} />)}
            {phone !== undefined && <Line label="Phone (verified)" value={phone} verified empty="Not verified yet" number />}
            {!fields.length && !others.length && phone === undefined && <Empty>The flow asks no questions.</Empty>}
          </Section>

          <Section title="Signed in">
            <Line label="Email" value={shown.signed_in ? shown.email ?? "Signed in" : null} verified empty="Not signed in" />
          </Section>

          <Section title="Policy">
            <Line
              label="Applies"
              value={shown.rule ? `${shown.rule.name} · ${shown.rule.action === "block" ? "Keeps them out" : "Lets them in"}` : "Everyone else"}
              danger={shown.rule?.action === "block"}
            />
          </Section>

          {detail.data && (
            <>
              <Section title="Known on">
                {detail.data.identities.map((identity) => (
                  <div key={`${identity.type}:${identity.external_id}`} className="flex items-center gap-2 py-1 text-[13px]">
                    <ViaIcon type={glyphOf(identity.type)} decorative />
                    <span className="text-[var(--text-primary)]">{nameOf(identity.type)}</span>
                    <span className="min-w-0 truncate text-[var(--text-tertiary)]" title={identity.external_id}>
                      {identity.name ?? identity.external_id}
                    </span>
                  </div>
                ))}
              </Section>
              {detail.data.apps.length > 0 && (
                <Section title="Apps">
                  {detail.data.apps.map(({ app, ...held }, i) => (
                    <div key={`${app.slug}:${i}`} className="flex items-center gap-2 py-1 text-[13px]">
                      <AppLogo logo={app.logo} name={app.name} size={18} iconSize={11} rounded={5} />
                      <span className="text-[var(--text-primary)]">{app.name}</span>
                      <span className="min-w-0 truncate text-[var(--text-tertiary)]">{connectedAs({ app, ...held })}</span>
                    </div>
                  ))}
                </Section>
              )}
              <Section title="Conversations">
                {detail.data.threads.length === 0 && <Empty>None yet.</Empty>}
                {detail.data.threads.map((thread) => (
                  <button
                    key={thread.id}
                    type="button"
                    onClick={() => onOpenThread?.(thread.id)}
                    className="press w-full flex items-center gap-2 px-2 -mx-2 py-1.5 rounded-[6px] text-left text-[13px] hover:bg-[var(--accent-hover)]"
                  >
                    <ViaIcon type={thread.via} decorative />
                    <span className="flex-1 min-w-0 truncate">{thread.title ?? "Conversation"}</span>
                    {thread.last_message_at && (
                      <span className="font-mono tabular-nums text-[11.5px] text-[var(--text-tertiary)]">{ago(thread.last_message_at)}</span>
                    )}
                  </button>
                ))}
              </Section>
            </>
          )}
        </div>

        <div className="px-5 py-3 border-t hairline flex flex-wrap gap-2">
          <GhostButton
            className="disabled:opacity-40 disabled:pointer-events-none"
            disabled={setStatus.isPending}
            onClick={() => {
              const status = shown.status === "blocked" ? "active" : "blocked";
              setStatus.mutate({ id: shown.id, status }, { onSuccess: () => toast.success(status === "blocked" ? "Blocked" : "Unblocked") });
            }}
          >
            {shown.status === "blocked" ? "Unblock" : "Block"}
          </GhostButton>
          <GhostButton onClick={() => setConfirm("restart")}>Restart onboarding</GhostButton>
          <div className="flex-1" />
          <button
            type="button"
            onClick={() => setConfirm("delete")}
            className="press rounded-full px-3.5 h-8 text-[13px] font-medium text-[var(--danger)] ring-1 ring-[var(--danger-ring)] hover:bg-[var(--danger-bg)]"
          >
            Delete
          </button>
        </div>
        {confirm && (
          <PersonConfirm
            person={shown}
            action={confirm}
            onClose={(done) => {
              setConfirm(null);
              if (done && confirm === "delete") onClose();
            }}
          />
        )}
      </SheetContent>
    </Sheet>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="section-label mb-1.5">{title}</div>
      {children}
    </div>
  );
}

/** A label and what they gave; `verified` ticks something they proved, and `empty` says
    what it means that they haven't. */
function Line({ label, value, verified = false, empty = "—", number = false, danger = false }: {
  label: string;
  value: string | null | undefined;
  verified?: boolean;
  empty?: string;
  /** A phone number: mono and tabular, as data is (DESIGN.md §11). */
  number?: boolean;
  /** What keeps them out, in red. */
  danger?: boolean;
}) {
  return (
    <div className="flex items-baseline gap-3 py-1 text-[13px]">
      <span className="w-[120px] shrink-0 text-[var(--text-tertiary)] truncate" title={label}>{label}</span>
      {value ? (
        <span className={`min-w-0 break-words ${danger ? "text-[var(--danger)]" : "text-[var(--text-primary)]"}`}>
          {verified && <span aria-label="verified" className="text-[var(--success)] mr-1.5">✓</span>}
          {number ? <span className="font-mono tabular-nums text-[12.5px]">{value}</span> : value}
        </span>
      ) : (
        <span className="text-[var(--text-quaternary)]">{empty}</span>
      )}
    </div>
  );
}

function Empty({ children }: { children: React.ReactNode }) {
  return <div className="text-[12px] text-[var(--text-quaternary)] italic">{children}</div>;
}
